import type { JSX } from 'react'
import { Calendar } from 'primereact/calendar'
import { InputNumber } from 'primereact/inputnumber'

import { FilterSidebar as FilterSidebarShell } from '../../data/FilterSidebar.js'
import { useAgents } from '../../../hooks/useAgents.js'
import { useContactCenterQueues } from '../../../hooks/useContactCenterQueues.js'
import type { RecordingFilters } from '../../../hooks/useRecordingsList.js'
import Select from '../../ui/Select.js'

interface RecordingsFilterSidebarProps {
  value: RecordingFilters
  onChange: (next: RecordingFilters) => void
}

/** Sentinel option value for "any" (null) in the native <select>s — the empty
 *  string maps back to `null` on the way into the filter object. */
const ANY = ''

/** Tri-state transcript option values. */
const TRANSCRIPT_ANY = ''
const TRANSCRIPT_YES = 'yes'
const TRANSCRIPT_NO = 'no'

/**
 * Controlled filter panel for the recordings library page. Does NOT own filter
 * state — every control calls `onChange({ ...value, <field>: ... })`.
 *
 * Composes the generic `<FilterSidebar>` shell from `components/data/` for the
 * aside chrome (title + clear button) and renders the recordings-specific
 * controls inside it.
 */
function RecordingsFilterSidebar({ value, onChange }: RecordingsFilterSidebarProps): JSX.Element {
  const { agents } = useAgents()
  const { items: queues } = useContactCenterQueues()

  /** Apply a partial patch over the current filter value. */
  const patch = (next: Partial<RecordingFilters>): void => {
    onChange({ ...value, ...next })
  }

  /** Clears every filter back to its null/empty state. */
  const clearAll = (): void => {
    onChange({
      fromDate: null,
      toDate: null,
      agentId: null,
      queueId: null,
      durationMinSec: null,
      durationMaxSec: null,
      hasTranscript: null,
    })
  }

  // PrimeReact's range Calendar emits `[Date, null]` after the FIRST click
  // (before the second date is picked). The filter only takes effect once both
  // ends are present — a one-ended (or null) range means "no date filter".
  const dateRange: [Date, Date | null] | null =
    value.fromDate ? [value.fromDate, value.toDate] : null

  const transcriptValue =
    value.hasTranscript === null
      ? TRANSCRIPT_ANY
      : value.hasTranscript
        ? TRANSCRIPT_YES
        : TRANSCRIPT_NO

  return (
    <FilterSidebarShell title="Filters" onClear={clearAll}>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-600">Date range</span>
        <Calendar
          aria-label="Filter by date range"
          value={dateRange}
          selectionMode="range"
          showIcon
          readOnlyInput
          placeholder="Any date"
          onChange={(e) => {
            const range = (e.value as [Date, Date | null] | null) ?? null
            patch({ fromDate: range?.[0] ?? null, toDate: range?.[1] ?? null })
          }}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-600">Agent</span>
                <Select
          ariaLabel="Filter by agent"
          size="sm"
          value={value.agentId ?? ANY}
          onChange={(v) => patch({ agentId: v === ANY ? null : v })}
        >
          <option value={ANY}>All agents</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              Ext. {a.extension}
            </option>
          ))}
                </Select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-600">Queue</span>
                <Select
          ariaLabel="Filter by queue"
          size="sm"
          value={value.queueId ?? ANY}
          onChange={(v) => patch({ queueId: v === ANY ? null : v })}
        >
          <option value={ANY}>All queues</option>
          {queues.map((q) => (
            <option key={q.id} value={q.id}>
              {q.name}
            </option>
          ))}
                </Select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-600">Min duration (sec)</span>
        <InputNumber
          inputId="recordings-filter-duration-min"
          aria-label="Minimum duration (seconds)"
          value={value.durationMinSec}
          min={0}
          placeholder="Any"
          onValueChange={(e) => patch({ durationMinSec: e.value ?? null })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-600">Max duration (sec)</span>
        <InputNumber
          inputId="recordings-filter-duration-max"
          aria-label="Maximum duration (seconds)"
          value={value.durationMaxSec}
          min={0}
          placeholder="Any"
          onValueChange={(e) => patch({ durationMaxSec: e.value ?? null })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-600">Transcript</span>
                <Select
          ariaLabel="Filter by transcript availability"
          size="sm"
          value={transcriptValue}
          onChange={(v) => {
            patch({
              hasTranscript:
                v === TRANSCRIPT_ANY ? null : v === TRANSCRIPT_YES,
            })
          }}
        >
          <option value={TRANSCRIPT_ANY}>Any</option>
          <option value={TRANSCRIPT_YES}>Has transcript</option>
          <option value={TRANSCRIPT_NO}>No transcript</option>
                </Select>
      </label>
    </FilterSidebarShell>
  )
}

export default RecordingsFilterSidebar
export { RecordingsFilterSidebar }
export type { RecordingsFilterSidebarProps }
